import { DatabaseField, DatabaseFieldType } from '@databases/types/database';
import {
  type DatabaseFilter,
  DatabaseFilterType,
  DatabaseFilterUsageType,
} from '@databases/types/database/view';
import { ValueChange } from '@databases/utils/database/changesets';
import {
  getInitialValue,
  getSupportedFilterTypes,
} from '@databases/utils/database/field-types';
import {
  ViewChangeType,
  ViewChangeTypes,
  ViewFilterChangeType,
} from '@databases/utils/database/view/view';
import { generateId } from '@databases/utils/utils';
import { updateFilterType } from '@databases/view-settings/updateFilterType';
import { useMemo, useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

// TODO NCTD-1989: Once we remove the old views experience, we can move these
// functions out of the hook since they'll only be needed in one place

export interface useFilterSettingsProps {
  onChange: (delta: ViewChangeType) => void;
  filterObject: DatabaseFilter | undefined;
  contentId: string;
  store: any;
}

export const useFilterSettings = ({
  onChange,
  filterObject,
  contentId,
  store,
}: useFilterSettingsProps) => {
  const filters = useMemo(
    () =>
      filterObject?.type === DatabaseFilterType.AND ||
      filterObject?.type === DatabaseFilterType.OR
        ? filterObject.value
        : filterObject
        ? [filterObject]
        : [],
    [filterObject]
  );

  const [draftFilterIds, setDraftFilterIds] = useState<string[]>([]);

  const handleChangeFilterType = useCallback(
    (type: DatabaseFilterUsageType) => {
      onChange({
        type: ViewFilterChangeType.VIEW_FILTERS_SET_FILTER_USAGE_TYPE,
        filterUsageType: type,
      });
    },
    [onChange]
  );

  const handleAddDraftFilter = useCallback(() => {
    setDraftFilterIds((ids) => [...ids, generateId()]);
  }, []);

  const handleRemoveDraftFilter = useCallback((filterId: string) => {
    setDraftFilterIds((ids) => ids.filter((id) => id !== filterId));
  }, []);

  const handleCreateDefaultFilter = useCallback(
    (fieldId?: string) => {
      onChange({
        type: ViewChangeTypes.VIEW_FILTERS_APPEND,
        filter: {
          type: DatabaseFilterType.EQUALS,
          id: uuidv4(),
          fieldId: fieldId ?? '',
          value: getInitialValue(DatabaseFieldType.TEXT),
        },
      });
    },
    [onChange]
  );

  const handleCreateFilter = useCallback(
    (filter: DatabaseFilter) => {
      onChange({
        type: ViewChangeTypes.VIEW_FILTERS_APPEND,
        filter,
      });
    },
    [onChange]
  );

  const handleFieldChange = useCallback(
    (field: DatabaseField | undefined) => {
      let newValue: DatabaseFilter = { ...filterObject, fieldId: field!.id } as any;
      const supportedTypes = getSupportedFilterTypes(field!.type);
      if (newValue.type && !supportedTypes.includes(newValue.type)) {
        newValue = updateFilterType(
          newValue,
          { type: supportedTypes[0] },
          contentId,
          store.getState()
        );
      }
      onChange({
        type: ViewFilterChangeType.VIEW_FILTERS_SET_FIELD_ID,
        filter: newValue,
      });
    },
    [contentId, filterObject, onChange, store]
  );

  const handleFilterTypeChange = useCallback(
    (filter: DatabaseFilter) => {
      onChange({
        type: ViewFilterChangeType.VIEW_FILTERS_SET_FILTER_TYPE,
        filter,
      });
    },
    [onChange]
  );

  const handleFilterValueChange = useCallback(
    (filter: DatabaseFilter, changes: (ValueChange | null)[]) => {
      if (filter.type !== DatabaseFilterType.EMPTY) {
        for (const change of changes) {
          onChange({
            type: ViewFilterChangeType.VIEW_FILTERS_SET_VALUE,
            filter,
            change,
          });
        }
      }
    },
    [onChange]
  );

  return {
    draftFilterIds,
    filters,
    handleAddDraftFilter,
    handleChangeFilterType,
    handleCreateDefaultFilter,
    handleCreateFilter,
    handleFieldChange,
    handleFilterTypeChange,
    handleFilterValueChange,
    handleRemoveDraftFilter,
  };
};
